<script setup lang="ts">
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import LegalFormDuplicateWarning from '@/components/app/cases/LegalFormDuplicateWarning.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { debounce } from '@/lib/timing'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import type { CustomerCase as Case } from '@/types/generated'
  import { CompanyLegalFormEnum, EntityTypeEnum, TitleEnum } from '@/types/generated'
  import { Building2, ChevronRightIcon, Landmark, User } from 'lucide-vue-next'
  import { computed } from 'vue'
  import { useRouter } from 'vue-router'

  const { t } = useTranslation({
    en: {
      clientData: 'Client Data',
      type: 'Type',
      title: 'Title',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      street: 'Street',
      houseNumber: 'House Number',
      zip: 'Postal Code',
      city: 'City',
      viewAllData: 'View all data',
      naturalPerson: 'Natural Person',
      legalPerson: 'Legal Person',
      institution: 'Institution',
      notSelected: 'Not selected',
      companyName: 'Company Name',
      companyLegalForm: 'Legal Form',
      institutionName: 'Institution Name',
      contactPerson: 'Contact Person',
      legalInsurance: 'Legal Insurance',
      legalInsuranceName: 'Insurance Name',
      legalInsuranceNumber: 'Insurance Number',
      legalInsuranceDeductible: 'Deductible Amount',
      addressLine2: 'Address Line 2'
    },
    de: {
      clientData: 'Mandanten Daten',
      type: 'Typ',
      title: 'Anrede',
      firstName: 'Vorname',
      lastName: 'Nachname',
      email: 'E-Mail',
      phone: 'Telefon',
      street: 'Straße',
      houseNumber: 'Hausnummer',
      zip: 'PLZ',
      city: 'Stadt',
      viewAllData: 'Alle Daten ansehen',
      naturalPerson: 'Natürliche Person',
      legalPerson: 'Juristische Person',
      institution: 'Institution',
      notSelected: 'Nicht ausgewählt',
      companyName: 'Firmenname',
      companyLegalForm: 'Rechtsform',
      institutionName: 'Institutionsname',
      contactPerson: 'Ansprechpartner',
      legalInsurance: 'Rechtsschutzversicherung',
      legalInsuranceName: 'Versicherungsname',
      legalInsuranceNumber: 'Versicherungsnummer',
      legalInsuranceDeductible: 'Selbstbeteiligung',
      addressLine2: 'Adresszeile 2'
    }
  })

  const caseStore = useCaseStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)
  const router = useRouter()

  // Type options for SelectField
  const typeOptions = computed(() => [
    {
      value: EntityTypeEnum.NATURAL_PERSON,
      title: t('naturalPerson'),
      icon: User
    },
    {
      value: EntityTypeEnum.LEGAL_PERSON,
      title: t('legalPerson'),
      icon: Building2
    },
    {
      value: EntityTypeEnum.INSTITUTION,
      title: t('institution'),
      icon: Landmark
    }
  ])

  // Title options for SelectField
  const titleOptions = computed(() =>
    Object.entries(TitleEnum).map(([key, value]) => ({
      value,
      title: value
    }))
  )

  // Legal form options for SelectField
  const legalFormOptions = computed(() =>
    Object.entries(CompanyLegalFormEnum).map(([key, value]) => ({
      value,
      title: value
    }))
  )

  // Debounced save function to prevent too many API calls
  // TODO: Consider Using Autosaving form component or making it work with this setup
  const saveClientData = debounce(async (key: string, value: any) => {
    try {
      const success = await caseStore.updateClientField(key, value)

      if (!success) {
        console.error('Failed to update client field:', key)
      }
    } catch (error) {
      console.error('Error saving entity:', error)
    }
  }, 1000)

  // Handle field changes
  const handleFieldChange = (field: string, value: any) => {
    if (!caseRecord.value?.data?.client) return

    // Save the value to the API using the store method
    saveClientData(field, value)
  }

  // Check if field has AI source
  const hasAISource = (field: string): boolean => {
    return false
  }

  const viewAllData = () => {
    if (caseStore.caseRecord?.uuid) {
      router.push(`/app/cases/${caseStore.caseRecord.uuid}/entities`)
    }
  }

  const handleFixCompanyName = async () => {
    if (!caseRecord.value?.data?.client) return

    const companyName = caseRecord.value.data.client.company_name || ''
    const legalForm = caseRecord.value.data.client.company_legal_form || ''

    if (!companyName || !legalForm) return

    const pattern = new RegExp(
      `[\\s,\\-]*${legalForm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`,
      'i'
    )
    const cleanedName = companyName.replace(pattern, '').trim()

    if (cleanedName) {
      caseRecord.value.data.client.company_name = cleanedName
      await caseStore.updateClientField('company_name', cleanedName)
    }
  }
</script>

<template>
  <div class="client-data-widget">
    <!-- Form Fields -->
    <div class="">
      <!-- Type -->
      <div class="mb-3">
        <SelectField
          v-if="caseRecord?.data"
          v-model="caseRecord.data.client.entity_type"
          :options="typeOptions"
          :label="t('type')"
          name="entity_type"
          class="w-full"
          @update:model-value="handleFieldChange('entity_type', $event)"
        >
          <template #prefix>
            <span class="mr-2 text-gray-500">
              <component
                :is="
                  typeOptions.find(
                    (option) => option.value === caseRecord?.data?.client.entity_type
                  )?.icon || User
                "
                class="h-4 w-4"
              />
            </span>
          </template>
        </SelectField>
      </div>

      <!-- LEGAL PERSON FIELDS -->
      <template v-if="caseRecord?.data?.client?.entity_type === EntityTypeEnum.LEGAL_PERSON">
        <!-- Company Name and Legal Form -->
        <div class="-mx-2 mb-3 flex flex-wrap">
          <div class="mb-3 w-full px-2 sm:mb-0 sm:w-3/4">
            <InputField
              v-if="caseRecord?.data?.client"
              v-model="caseRecord.data.client.company_name"
              :label="t('companyName')"
              :ai-source="hasAISource('company_name')"
              name="company_name"
              class="w-full"
              @update:model-value="handleFieldChange('company_name', $event)"
            />
          </div>
          <div class="w-full px-2 sm:w-1/4">
            <SelectField
              v-if="caseRecord?.data?.client"
              v-model="caseRecord.data.client.company_legal_form"
              :options="legalFormOptions"
              :label="t('companyLegalForm')"
              :ai-source="hasAISource('company_legal_form')"
              name="company_legal_form"
              class="w-full"
              @update:model-value="handleFieldChange('company_legal_form', $event)"
            />
          </div>
        </div>

        <LegalFormDuplicateWarning
          :company-name="caseRecord?.data?.client?.company_name || ''"
          :legal-form="caseRecord?.data?.client?.company_legal_form || ''"
          :on-fix="handleFixCompanyName"
        />

        <!-- Contact Person Section -->
      </template>

      <!-- INSTITUTION FIELDS -->
      <template v-else-if="caseRecord?.data?.client?.entity_type === EntityTypeEnum.INSTITUTION">
        <!-- Institution Name -->
        <div class="-mx-2 mb-3 flex">
          <div class="w-full px-2">
            <InputField
              v-if="caseRecord?.data?.client?.entity_type === EntityTypeEnum.INSTITUTION"
              v-model="caseRecord.data.client.institution_name"
              :label="t('institutionName')"
              :ai-source="hasAISource('institution_name')"
              name="institution_name"
              class="w-full"
              @update:model-value="handleFieldChange('institution_name', $event)"
            />
          </div>
        </div>

        <!-- Contact Person Section -->
      </template>

      <!-- COMMON FIELDS FOR ALL ENTITY TYPES OR NATURAL PERSON ONLY -->
      <!-- Title, First Name, Last Name in a row -->
      <div class="-mx-2 mb-3 flex">
        <div class="xs:w-1/2 mb-3 w-full px-2 sm:mb-0 sm:w-1/3">
          <SelectField
            v-if="caseRecord?.data?.client"
            v-model="caseRecord.data.client.title"
            :options="titleOptions"
            :label="t('title')"
            :ai-source="hasAISource('title')"
            :select-option-text="t('title')"
            name="title"
            placeholder="Nicht ausgewählt"
            class="w-full"
            @update:model-value="handleFieldChange('title', $event)"
          />
        </div>
        <div class="mb-3 w-1/2 px-2 sm:mb-0 sm:w-1/3">
          <InputField
            v-if="caseRecord?.data?.client"
            v-model="caseRecord.data.client.first_name"
            :label="t('firstName')"
            :ai-source="hasAISource('first_name')"
            name="first_name"
            class="w-full"
            @update:model-value="handleFieldChange('first_name', $event)"
          />
        </div>
        <div class="xs:w-1/2 w-full px-2 sm:w-5/12">
          <InputField
            v-if="caseRecord?.data?.client"
            v-model="caseRecord.data.client.last_name"
            :label="t('lastName')"
            :ai-source="hasAISource('last_name')"
            name="last_name"
            class="w-full"
            @update:model-value="handleFieldChange('last_name', $event)"
          />
        </div>
      </div>

      <!-- Email and Phone in a row -->
      <div class="-mx-2 mb-3 flex flex-wrap">
        <div class="mb-3 w-full px-2 sm:mb-0 sm:w-1/2">
          <InputField
            v-if="caseRecord?.data?.client"
            v-model="caseRecord.data.client.email"
            :label="t('email')"
            :ai-source="hasAISource('email')"
            name="email"
            type="email"
            class="w-full"
            @update:model-value="handleFieldChange('email', $event)"
          />
        </div>
        <div class="w-full px-2 sm:w-1/2">
          <InputField
            v-if="caseRecord?.data?.client"
            v-model="caseRecord.data.client.phone"
            :label="t('phone')"
            :ai-source="hasAISource('phone')"
            name="phone"
            class="w-full"
            @update:model-value="handleFieldChange('phone', $event)"
          />
        </div>
      </div>

      <!-- Address Section - Common for all entity types -->
      <!-- Street and House Number in a row -->
      <div class="-mx-2 mb-3 flex">
        <div class="mb-3 w-full px-2 sm:mb-0 sm:w-3/4">
          <InputField
            v-if="caseRecord?.data?.client"
            v-model="caseRecord.data.client.street"
            :label="t('street')"
            :ai-source="hasAISource('street')"
            name="street"
            class="w-full"
            @update:model-value="handleFieldChange('street', $event)"
          />
        </div>
        <div class="w-full min-w-[33%] px-2 sm:w-1/4">
          <InputField
            v-if="caseRecord?.data?.client"
            v-model="caseRecord.data.client.street_number"
            :label="t('houseNumber')"
            :ai-source="hasAISource('street_number')"
            name="street_number"
            class="w-full"
            @update:model-value="handleFieldChange('street_number', $event)"
          />
        </div>
      </div>

      <!-- ZIP and City in a row -->
      <div class="-mx-2 mb-3 flex">
        <div class="mb-3 min-w-[33%] grow-0 px-2 sm:mb-0">
          <InputField
            v-if="caseRecord?.data?.client"
            v-model="caseRecord.data.client.zip"
            :label="t('zip')"
            :ai-source="hasAISource('zip')"
            name="zip"
            class="w-full"
            @update:model-value="handleFieldChange('zip', $event)"
          />
        </div>
        <div class="xs:w-2/3 w-full grow px-2">
          <InputField
            v-if="caseRecord?.data?.client"
            v-model="caseRecord.data.client.city"
            :label="t('city')"
            :ai-source="hasAISource('city')"
            name="city"
            class="w-full"
            @update:model-value="handleFieldChange('city', $event)"
          />
        </div>
      </div>

      <!-- Address Line 2 field -->
      <div class="-mx-2 mb-3 flex">
        <div class="w-full px-2 sm:w-1/3">
          <InputField
            v-if="caseRecord?.data?.client"
            v-model="caseRecord.data.client.address_line_2"
            :label="t('addressLine2')"
            :ai-source="hasAISource('address_line_2')"
            name="address_line_2"
            class="w-full"
            @update:model-value="handleFieldChange('address_line_2', $event)"
          />
        </div>
      </div>

      <!-- View All Data Link -->
      <div class="mt-4 text-right">
        <a
          href="#"
          class="inline-flex items-center text-sm font-medium text-purple-600 underline hover:text-purple-800"
          @click.prevent="viewAllData"
        >
          {{ t('viewAllData') }}
          <ChevronRightIcon class="ml-1 h-4 w-4" />
        </a>
      </div>
    </div>
  </div>
</template>
<style scoped>
  .dots-menu {
    letter-spacing: -1px;
    transform: rotate(90deg);
    display: inline-block;
    font-weight: bold;
    font-size: 14px;
  }
</style>
